import React, { useEffect, useState, useCallback } from "react";
import { Container, Row, Col, Form, Button, Alert } from "react-bootstrap";
import { AsyncPaginate } from "react-select-async-paginate";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../axiosConfig";
import AccountPickerModal from "../pickers/AccountPickerModal";
import CostCenterPickerModal from "../pickers/CostCenterPickerModal";
const toItems = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

export default function ContCcMapCreate() {
  const navigate = useNavigate();
  const location = useLocation();

  const preset = location.state || {};
  const [cont, setCont] = useState(null);
  const [cc, setCc] = useState(null);

  const [busy, setBusy] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  const [showCont, setShowCont] = useState(false);
  const [showCc, setShowCc] = useState(false);

  const [err, setErr] = useState("");

  const loadAccounts = useCallback(async (search, loaded, { page }) => {
    const q = encodeURIComponent(search || "");
    const p = page || 1;

    const data = await api
      .get(`/accounts?q=${q}&page=${p}&limit=20`)
      .then((r) => r.data);
    const items = toItems(data);

    const options = items.map((x) => {
      const code = x.code ?? x.cod ?? "";
      const name = x.name ?? x.denumire ?? "";
      return { value: x.id, code, label: `${code} • ${name}` };
    });

    return {
      options,
      hasMore: options.length === 20,
      additional: { page: p + 1 },
    };
  }, []);

  const loadCostCenters = useCallback(async (search, loaded, { page }) => {
    const q = encodeURIComponent(search || "");
    const p = page || 1;

    const data = await api
      .get(`/cost_centers?q=${q}&page=${p}&limit=20`)
      .then((r) => r.data);
    const items = toItems(data);

    const options = items.map((x) => {
      const code = x.code ?? x.cod ?? "";
      const name = x.name ?? x.denumire ?? "";
      return {
        value: x.id,
        code,
        label: code ? `${code} • ${name}` : `${x.id} • ${name}`,
      };
    });

    return {
      options,
      hasMore: options.length === 20,
      additional: { page: p + 1 },
    };
  }, []);

  // preselect dacă ai venit din listă cu cont_id / cost_center_id
  useEffect(() => {
    if (preset?.cont_id && !cont) {
      // minim: setăm value; label va fi corect după ce utilizatorul caută sau dacă vrei, facem fetch /accounts/{id}
      setCont({ value: preset.cont_id, label: `Cont #${preset.cont_id}` });
    }
    if (preset?.cost_center_id && !cc) {
      setCc({
        value: preset.cost_center_id,
        label: `CC #${preset.cost_center_id}`,
      });
    }
  }, [preset, cont, cc]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setErr("");

    if (!cont?.id || !cc?.id) {
      setErr("Alege cont și centru de cost.");
      return;
    }

    try {
      setSubmitting(true);

      await api.post("/cont_cc_map", {
        cont_id: cont.id,
        cost_center_id: cc.id,
      });

      navigate("/cont-cc-map");
    } catch (err) {
      setErr(
        err?.response?.data?.detail ||
          err?.response?.data?.errors?.[0]?.msg ||
          err?.message ||
          "Eroare la salvare",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container className="py-3">
      <Row className="mb-3">
        <Col>
          <h5 className="mb-0">Adaugă mapare cont → centru de cost</h5>
          <div className="small text-muted">
            Completează câmpurile și salvează.
          </div>
        </Col>
      </Row>

      {err ? <Alert variant="danger">{err}</Alert> : null}

      <Form onSubmit={handleCreate}>
        <Row className="g-3">
          {/* RÂND 1 – CONT */}
          <Col xs={12}>
            <Form.Group className="mb-3">
              <Form.Label>Cont</Form.Label>

              <div
                className="form-control d-flex align-items-center justify-content-between"
                style={{ cursor: "pointer" }}
                onClick={() => setShowCont(true)}
              >
                <span className={cont ? "" : "text-muted"}>
                  {cont
                    ? `${cont.code ?? cont.cod ?? ""} • ${
                        cont.denumire ?? cont.name ?? ""
                      }`
                    : "Click pentru a alege cont…"}
                </span>

                {cont ? (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setCont(null);
                    }}
                  >
                    ✕
                  </span>
                ) : (
                  <span className="text-muted">▾</span>
                )}
              </div>
            </Form.Group>
          </Col>

          {/* RÂND 2 – CENTRU COST */}
          <Col xs={12}>
            <Form.Group className="mb-3">
              <Form.Label>Centru de cost</Form.Label>

              <div
                className="form-control d-flex align-items-center justify-content-between"
                style={{
                  cursor: cont ? "pointer" : "not-allowed",
                  opacity: cont ? 1 : 0.6,
                }}
                onClick={() => cont && setShowCc(true)}
              >
                <span className={cc ? "" : "text-muted"}>
                  {cc
                    ? `${cc.code ?? ""} • ${cc.name ?? ""}`
                    : "Click pentru a alege centru cost…"}
                </span>

                {cc ? (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setCc(null);
                    }}
                  >
                    ✕
                  </span>
                ) : (
                  <span className="text-muted">▾</span>
                )}
              </div>
            </Form.Group>
          </Col>
        </Row>

        <div className="text-end">
          <Button type="submit" disabled={!cont || !cc || submitting}>
            {submitting ? "Se salvează…" : "Salvează"}
          </Button>
        </div>
      </Form>
      <AccountPickerModal
        show={showCont}
        onHide={() => setShowCont(false)}
        onSelect={(acc) => {
          setCont(acc);
        }}
      />

      <CostCenterPickerModal
        show={showCc}
        onHide={() => setShowCc(false)}
        onSelect={(c) => {
          setCc(c);
        }}
      />
    </Container>
  );
}
